import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { fn } from '@ember/helper'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import type { RosteringDestination } from 'district-ui-client/services/external-rostering'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type School from 'district-ui-client/models/school'
import { ThemedLink } from 'district-ui-client/components/themed-button'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { FormComponentsFormSelect } from 'district-ui-client/components/form-components/form-select'
import type { SelectOption } from 'district-ui-client/components/form-components/form-select/option'
import { hasId } from 'district-ui-client/utils/type-guards'

interface SchoolOption extends SelectOption {
  uid: string
  id: string
  text: string
  value: string
}

// the destinations this panel has copy for
type RedirectPanelDestination = Extract<RosteringDestination, 'manage-teachers' | 'manage-students' | 'manage-classes'>

interface Signature {
  Element: HTMLElement
  Args: {
    schools?: Nullable<School[]>
    destination: RedirectPanelDestination
    // what Roster links back to on exit rostering
    program: SubscriptionType
  }
}

/**
 * For 3P Roster (OneRoster) enabled districts, teacher/student management happens in Roster.
 */
export class RosterRedirectPanel extends Component<Signature> {
  @service externalRostering!: ExternalRosteringService

  @tracked selectedSchoolId = ''

  get subtextKey(): string {
    switch (this.args.destination) {
      case 'manage-teachers':
        return 'components.homePage.rosterRedirect.teachers.title'
      case 'manage-students':
        return 'components.homePage.rosterRedirect.students.title'
      case 'manage-classes':
        return 'components.homePage.rosterRedirect.classes.title'
    }
  }

  get continueKey(): string {
    switch (this.args.destination) {
      case 'manage-teachers':
        return 'components.homePage.rosterRedirect.teachers.continue'
      case 'manage-students':
        return 'components.homePage.rosterRedirect.students.continue'
      case 'manage-classes':
        return 'components.homePage.rosterRedirect.classes.continue'
    }
  }

  get sortedSchoolsOptions(): SchoolOption[] {
    return [...(this.args.schools ?? [])]
      .filter((school) => school.oneRosterEnabledAt)
      .filter(hasId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((school) => ({ uid: school.id, id: school.id, text: school.name, value: school.id }))
  }

  get selectedSchoolOption(): SchoolOption | undefined {
    return this.sortedSchoolsOptions.find((option) => option.id === this.selectedSchoolId)
  }

  get submitDisabled(): boolean {
    return !this.selectedSchoolId
  }

  get rosterUrl(): string | undefined {
    if (!this.selectedSchoolId) return undefined

    return this.externalRostering.oneRosterUrl(this.selectedSchoolId, this.args.destination, this.args.program)
  }

  selectSchool = (option?: SchoolOption) => {
    this.selectedSchoolId = option?.id ?? ''
  }

  <template>
    <div class="space-y-4" ...attributes>
      <p data-test-roster-redirect-subtext>{{t this.subtextKey}}</p>
      <div class="max-w-md space-y-4">
        <FormComponentsFormSelect
          data-test-select-school
          class="w-full"
          @defaultText={{t "components.homePage.selectSchoolPlaceholder"}}
          @search={{true}}
          @searchPlaceholder={{t "searchPlaceholder.schools"}}
          @options={{this.sortedSchoolsOptions}}
          @optionClick={{this.selectSchool}}
          @value={{this.selectedSchoolOption}}
          @resetAction={{fn this.selectSchool undefined}}
        />
        <ThemedLink
          data-test-school-picker-submit
          class="flex w-full items-center justify-center"
          @style="theme"
          @disabled={{this.submitDisabled}}
          href={{this.rosterUrl}}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>{{t this.continueKey}}</span>
          <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
        </ThemedLink>
      </div>
    </div>
  </template>
}

export default RosterRedirectPanel
